import React, { JSX, ReactElement } from "react";
import Router from "next/router";
import NProgress from "nprogress";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import App, {
  AppProps,
  AppInitialProps,
  AppContext as NextAppContext,
} from "next/app";
import Head from "next/head";
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from "@apollo/client";
import { NextComponentType } from "next";
import {
  fetchNavigationSets,
  NavigationSetWithFlag,
} from "utils/fetchNavigationSets";
import { endpoints, isLocalEnv } from "config";
import { AppContext } from "context/app";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { CartContext } from "context/cart";
import { LogContext } from "context/log";
import { useLogProvider } from "context/hooks/useLogProvider";
import { useAppProvider } from "context/hooks/useAppProvider";
import { useUserProvider } from "context/hooks/useUserProvider";
import { useCartProvider } from "context/hooks/useCartProvider";
import { useModalProvider } from "context/hooks/useModalProvider";
import { useFontTracking } from "@pepdirect/v3/hooks/useFontTracking";
import { FontTrackingiFrame } from "@pepdirect/v3/ui/FontTrackingiFrame";
import { Ccm } from "@pepdirect/v3/ui/Ccm";
import { shouldForwardProp } from "@pepdirect/v3/helpers/transientProps";

import "styles/index.scss";

/* Customizing swiper */
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "swiper/css/zoom";
import "styles/swiper.scss";

/* Customizing modal */
import "styles/modal.scss";

import { GatoradeTeamZoneTheme } from "@pepdirect/v3/theme/GatoradeTeamZoneTheme";
import { RootModal } from "components/Modals/Modal";

/* Importing and customizing nprogress */
import "styles/nprogress.scss";
import {
  META_TITLE,
  META_DESCRIPTION,
  META_DEFAULT_OG_IMAGE,
  CANONICAL_URL_BASE,
  PRIMARY_COLOR,
} from "constants/meta";

import "styles/stylingOverrides.scss";
import {
  isPublicRoute,
  isServerSide,
  serverSideRedirect,
} from "helpers/routing";
import { isLoggedIn } from "helpers/user";
import { addGitShaToWindow } from "@pepdirect/v3/helpers/gitSha";
import { generateFontFaces } from "@pepdirect/v3/helpers/fonts";
import {
  CurrentUserDocument,
  CurrentUserQuery,
  TenantDocument,
  TenantQuery,
  TenantQueryVariables,
} from "services/graphql/generated";
import client from "services/graphql/client";
import createSsrClient from "services/graphql/ssr-client";
import { AnalyticsSessionParamsContext } from "context/analyticsSessionParams";
import { useAnalyticsSessionParamsProvider } from "context/hooks/useAnalyticsSessionParamsProvider";
import { useTagsProvider } from "context/hooks/useTagsProvider";
import { TagsContext } from "context/category";
addGitShaToWindow();

// Init progress bar
NProgress.configure({ showSpinner: false });
Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

// Scroll to top after router.push();
Router.events.on("routeChangeComplete", () => window?.scrollTo(0, 0));

interface TeamzoneAppInitialProps extends AppInitialProps {
  tenant: TenantQuery;
  navigationSets: NavigationSetWithFlag[];
  user: CurrentUserQuery["currentUser"] | null;
}

interface TeamzoneAppProps extends AppProps {
  tenant: TenantQuery;
  navigationSets: NavigationSetWithFlag[];
  user: CurrentUserQuery["currentUser"] | null;
}

Teamzone.getInitialProps = async (
  appContext: NextAppContext
): Promise<TeamzoneAppInitialProps> => {
  let gqlClient: ApolloClient<NormalizedCacheObject>;
  let user: CurrentUserQuery["currentUser"] | null = null;

  if (isServerSide(appContext)) {
    gqlClient = createSsrClient(appContext?.ctx?.req);
  } else {
    gqlClient = client;
  }

  try {
    const userData = await gqlClient.query<CurrentUserQuery>({
      query: CurrentUserDocument,
    });
    user = userData?.data?.currentUser;
  } catch (e) {
    // logged in errorLink
  }

  if (isServerSide(appContext) && !isLoggedIn(user)) {
    /* Redirect logged-out users on server side */
    if (!isPublicRoute(appContext.router.pathname)) {
      serverSideRedirect(appContext, "/sign-in");
    }
  }

  // When we add cookies to the request for tenant query,
  // if the cookie is not valid, then they return 401 unauthenticated error.
  // To prevent this, we detach cookies from the request before making request to them
  // if the user cookie is not valid. If cookie is not set, then this would not be a problem.
  if (!isLoggedIn(user) && isServerSide(appContext)) {
    gqlClient = createSsrClient(appContext?.ctx?.req, true);
  }

  /* TODO: maybe have some backup values, in case getTenant fails? */
  /* eslint-disable no-useless-catch */
  try {
    const [appProps, tenantData, navigationSets] = await Promise.all([
      App.getInitialProps(appContext),
      gqlClient.query<TenantQuery, TenantQueryVariables>({
        query: TenantDocument,
      }),
      fetchNavigationSets(gqlClient),
    ]);

    return {
      ...appProps,
      user,
      tenant: tenantData?.data,
      navigationSets,
    };
  } catch (e) {
    // make sure to re-throw after catch logs in errorLink so we show 500 error
    throw e;
  }
};

// Revisit this later if layout needs to change https://adamwathan.me/2019/10/17/persistent-layout-patterns-in-nextjs/
export default function Teamzone({
  Component,
  pageProps,
  tenant,
  navigationSets,
  user,
}: TeamzoneAppProps): JSX.Element {
  const fonts = tenant.tenant?.brandConfig.trackingFonts || [];
  const title = tenant.tenant?.brandConfig.title || "Gatorade TeamZone";
  const trackFontResources = useFontTracking(fonts, title);

  const { appContextValue } = useAppProvider(tenant.tenant);
  const { modalContextValue } = useModalProvider();
  const { userContextValue } = useUserProvider(user, client);
  const { tagsContextValue } = useTagsProvider(navigationSets);
  const { useLogPageView } = useLogProvider(userContextValue.currentUserId);
  const { analyticsParamsValue } = useAnalyticsSessionParamsProvider();
  const { cartContextValue } = useCartProvider(
    client,
    userContextValue.currentUserId
  );

  const getLayout =
    (
      Component as NextComponentType & {
        getLayout: (page: ReactElement) => ReactElement;
      }
    ).getLayout || ((page) => page);

  return (
    <>
      <Head>
        {/* Meta tags not intended to change */}
        <meta name="theme-color" content={PRIMARY_COLOR} />
        <meta name="og:site_name" content={META_TITLE} />
        <meta name="og:price:currency" content="USD" />
        {/* Meta tags */}
        <meta name="og:title" content={META_TITLE} key="og:title" />
        <meta name="description" content={META_DESCRIPTION} key="description" />
        <meta
          name="og:description"
          content={META_DESCRIPTION}
          key="og:description"
        />
        <meta name="og:image" content={META_DEFAULT_OG_IMAGE} key="og:image" />
        <meta name="og:type" content="website" key="og:type" />
        <meta name="og:url" content={CANONICAL_URL_BASE} key="og:url" />
      </Head>
      <Ccm cdn={endpoints.cdn} />
      <style
        dangerouslySetInnerHTML={{
          __html: generateFontFaces(
            tenant?.tenant?.brandConfig?.customizations.fonts || []
          ),
        }}
      />
      {trackFontResources.map((resource) => (
        <FontTrackingiFrame
          resource={resource}
          key={resource}
          isLocalEnv={isLocalEnv}
        />
      ))}
      <ApolloProvider client={client}>
        <AppContext.Provider value={appContextValue}>
          <ThemeProvider theme={GatoradeTeamZoneTheme}>
            <ModalContext.Provider value={modalContextValue}>
              <UserContext.Provider value={userContextValue}>
                <CartContext.Provider value={cartContextValue}>
                  <LogContext.Provider value={{ useLogPageView }}>
                    <AnalyticsSessionParamsContext.Provider
                      value={analyticsParamsValue}
                    >
                      <TagsContext.Provider value={tagsContextValue}>
                        <StyleSheetManager
                          shouldForwardProp={shouldForwardProp}
                        >
                          <div className="page-layout">
                            {getLayout(<Component {...pageProps}></Component>)}
                          </div>
                          <RootModal />
                        </StyleSheetManager>
                      </TagsContext.Provider>
                    </AnalyticsSessionParamsContext.Provider>
                  </LogContext.Provider>
                </CartContext.Provider>
              </UserContext.Provider>
            </ModalContext.Provider>
          </ThemeProvider>
        </AppContext.Provider>
      </ApolloProvider>
    </>
  );
}
